import { JobApplication } from '../models/job-application.model';
import { BaseService } from '../../../../../core/services/base.service';

export const jobApplicationScopeFilter = (
  scope: 'active' | 'archived'
): string => (scope === 'archived' ? 'deleted:eq:true' : 'active:eq:true');

export class JobApplicationService {
  static async GetActiveApplications(params?: {
    page?: number;
    size?: number;
    sort?: string;
    filter?: string;
  }): Promise<{ content: JobApplication[]; totalElements: number }> {
    const query = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : '';
    const result = await BaseService.Get(`/job-applications${query}`);
    return {
      content: Array.isArray(result?.content) ? result.content : [],
      totalElements: result?.totalElements ?? 0,
    };
  }

  static async GetApplications(
    companyId: string,
    params?: {
      page?: number;
      size?: number;
      sort?: string;
      filter?: string;
    }
  ): Promise<{ content: JobApplication[]; totalElements: number }> {
    const query = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : '';

    const result = await BaseService.Get(
      `/companies/${companyId}/job-applications${query}`
    );
    return {
      content: Array.isArray(result?.content) ? result.content : [],
      totalElements: result?.totalElements ?? 0,
    };
  }

  static async GetApplicationsById(
    id: string,
    params?: {
      page?: number;
      size?: number;
      sort?: string;
      filter?: string;
    }
  ): Promise<{ content: JobApplication[]; totalElements: number }> {
    const activeFilter = [jobApplicationScopeFilter('active'), params?.filter]
      .filter(Boolean)
      .join(',');
    const activeParams = new URLSearchParams();
    if (params?.page != null) {
      activeParams.set('page', String(params.page));
    }
    if (params?.size != null) {
      activeParams.set('size', String(params.size));
    }
    if (params?.sort) {
      activeParams.set('sort', params.sort);
    }
    activeParams.set('filter', activeFilter);
    const query = `?${activeParams.toString()}`;

    const result = await BaseService.Get(
      `/featured/jobs/${id}/job-applications${query}`
    );
    return {
      content: Array.isArray(result?.content) ? result.content : [],
      totalElements: result?.totalElements ?? 0,
    };
  }

  static async GetById(
    id: string,
    CallbackCatch?: (error: Error) => void
  ): Promise<JobApplication> {
    return await BaseService.Get(
      `/job-applications/${id}`,
      CallbackCatch,
      false
    );
  }

  static async GetFile(id: string, fileId: string): Promise<Blob> {
    const segment = `/job-applications/${id}/files/${fileId}`;
    return await BaseService.Blob('GET', segment, undefined, undefined, false);
  }

  static async ExportApplication(id: string, jobId: string): Promise<Blob> {
    return await BaseService.Blob(
      'GET',
      `/job-applications/${id}/export?jobId=${encodeURIComponent(jobId)}`,
      undefined,
      undefined,
      false
    );
  }

  static async ExportSelected(
    jobId: string,
    applicationIds: string[]
  ): Promise<Blob> {
    return await BaseService.Blob(
      'POST',
      '/job-applications/export',
      { jobId, applicationIds },
      undefined,
      false
    );
  }

  static async AssignCandidate(jobApplicationId: string): Promise<void> {
    return await BaseService.Post(
      `/job-applications/${jobApplicationId}/candidates`,
      {}
    );
  }

  static async Restore(id: string): Promise<void> {
    return await BaseService.Patch(`/job-applications/${id}/restore`);
  }
}
